import React from 'react';
import {
  AimOutlined,
  FileSearchOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import { Button, Tooltip } from 'antd';
import { CREATING, FROZEN, PAUSED, type Session } from 'state';

import {
  ordinaryQuoteIsAvailable,
  ordinaryTradeIsDisabled,
  sessionBlocksTrading,
} from './model';
import type { MarketInstrument } from './types';

type Props = {
  row: MarketInstrument;
  session: Session;
  selected: boolean;
  onTrade: () => void;
  onFocus: () => void;
  onODA?: () => void;
  onDetails?: () => void;
};

const stop = (callback: () => void) => (event: React.MouseEvent) => {
  event.stopPropagation();
  callback();
};

const InstrumentActions: React.FC<Props> = ({
  row,
  session,
  selected,
  onTrade,
  onFocus,
  onODA,
  onDetails,
}) => {
  const tradeDisabled = ordinaryTradeIsDisabled({
    status: session.status,
    asset: row.asset,
    instrument: row.instrument,
    closedFromInterval: row.closedFromInterval,
  });
  const isMatured =
    row.asset.resourcetype === 'AFSBond' &&
    row.instrument.htm_afs_years_to_maturity === 0;
  const tradeReason = sessionBlocksTrading(session.status)
    ? 'Trading is unavailable while this session is paused or closed.'
    : !ordinaryQuoteIsAvailable(row.asset)
      ? 'This instrument is visible now and becomes tradable when its first quote is issued.'
      : isMatured
        ? 'This bond has matured.'
        : row.closedFromInterval
          ? 'Trading is closed for this interval.'
          : 'Trade this instrument';

  const odaDisabled =
    session.status === PAUSED ||
    session.status === CREATING ||
    session.status === FROZEN ||
    !row.instrument.amount;

  return (
    <div className="markets-v2-actions">
      <Tooltip title={tradeReason}>
        <span>
          <Button
            size="small"
            type="primary"
            icon={<SwapOutlined />}
            disabled={tradeDisabled}
            onClick={stop(onTrade)}
          >
            Trade
          </Button>
        </span>
      </Tooltip>
      {onODA && (
        <Tooltip title="Open order automation">
          <span>
            <Button size="small" disabled={odaDisabled} onClick={stop(onODA)}>
              ODA
            </Button>
          </span>
        </Tooltip>
      )}
      {onDetails && (
        <Tooltip title="Instrument details">
          <Button
            size="small"
            aria-label={`View ${row.asset.name || row.asset.ticker} details`}
            icon={<FileSearchOutlined />}
            onClick={stop(onDetails)}
          />
        </Tooltip>
      )}
      <Tooltip title="Focus the market charts on this instrument">
        <Button
          size="small"
          type={selected ? 'default' : 'text'}
          className={selected ? 'is-selected' : ''}
          aria-label={`Focus ${row.asset.name || row.asset.ticker}`}
          icon={<AimOutlined />}
          onClick={stop(onFocus)}
        />
      </Tooltip>
    </div>
  );
};

export default InstrumentActions;
